/**
 * Phase 7-F.2 — Adjudicator dashboard skeleton.
 *
 * Minimal cut: shows membership state, the configured adjudicator
 * allowlist, and a placeholder for the dispute queue. The full disputes /
 * compose-resolution / multisig coordination UX is deferred to a follow-up
 * phase (was deferred from 7-F.2 sub-agent attempts that hit Anthropic
 * stream-watchdog timeouts; see _workspace/00_input.md §1).
 *
 * The 7-F architect doc (`_workspace/phase-7-F-archive/01_architecture.md
 * §4.3`) is the authoritative spec for the full screen. This skeleton
 * preserves the route + sidebar gate so when the deferred UX ships, the
 * routing, locale strings, and membership gate are already in place.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAdjudicatorMembership } from '../../hooks/useAdjudicatorMembership';

export default function AdjudicatorDashboardScreen() {
  const { t } = useTranslation('adjudicator');
  const { isAdjudicator, contracts, loading, error, refetch } = useAdjudicatorMembership();

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">{t('dashboard.loading', 'Loading membership…')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-2">{t('dashboard.title', 'Adjudicator Dashboard')}</h1>
        <p className="text-sm text-red-600">
          {t('dashboard.error', 'Failed to fetch adjudicator membership: ')} {error}
        </p>
        <button
          onClick={refetch}
          className="mt-3 px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
        >
          {t('dashboard.retry', 'Retry')}
        </button>
      </div>
    );
  }

  if (!isAdjudicator) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-2">{t('dashboard.title', 'Adjudicator Dashboard')}</h1>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm">
          <p className="font-medium text-amber-900">
            {t('dashboard.notAdjudicator.title', 'You are not an adjudicator on this deployment.')}
          </p>
          <p className="text-amber-800 mt-1">
            {t(
              'dashboard.notAdjudicator.body',
              'The adjudicator role is granted at deploy time. Contact the platform admin to be added.',
            )}
          </p>
          <div className="mt-3">
            <Link to="/" className="text-amber-900 underline text-xs">
              {t('dashboard.backHome', 'Back to home')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('dashboard.title', 'Adjudicator Dashboard')}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {t(
            'dashboard.subtitle',
            'You hold the ADJUDICATOR_ROLE on this deployment. The full dispute queue + signature flow is deferred to a follow-up release.',
          )}
        </p>
      </div>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          {t('dashboard.kpi.title', 'Status')}
        </h2>
        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-md border border-gray-200 p-3">
            <dt className="text-xs text-gray-500">
              {t('dashboard.kpi.openDisputes', 'Open disputes')}
            </dt>
            <dd className="text-2xl font-semibold mt-1">—</dd>
            <p className="text-[11px] text-gray-400 mt-1">
              {t('dashboard.kpi.deferredHint', 'Wired in next release')}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 p-3">
            <dt className="text-xs text-gray-500">
              {t('dashboard.kpi.myPendingSigs', 'My pending signatures')}
            </dt>
            <dd className="text-2xl font-semibold mt-1">—</dd>
            <p className="text-[11px] text-gray-400 mt-1">
              {t('dashboard.kpi.deferredHint', 'Wired in next release')}
            </p>
          </div>
          <div className="rounded-md border border-gray-200 p-3">
            <dt className="text-xs text-gray-500">
              {t('dashboard.kpi.decisionsThisMonth', 'Decisions this month')}
            </dt>
            <dd className="text-2xl font-semibold mt-1">—</dd>
            <p className="text-[11px] text-gray-400 mt-1">
              {t('dashboard.kpi.deferredHint', 'Wired in next release')}
            </p>
          </div>
        </dl>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
          {t('dashboard.contracts.title', 'Adjudicator allowlist')}
        </h2>
        <p className="text-xs text-gray-500 mt-1">
          {t(
            'dashboard.contracts.subtitle',
            'Granted ADJUDICATOR_ROLE on the deployed escrow contract.',
          )}
        </p>
        <ul className="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200 overflow-hidden">
          {(contracts || []).map((addr) => (
            <li key={addr} className="px-3 py-2 text-xs font-mono break-all bg-white">
              {addr}
            </li>
          ))}
          {(contracts || []).length === 0 && (
            <li className="px-3 py-2 text-xs text-gray-500">
              {t('dashboard.contracts.empty', 'No adjudicator addresses configured.')}
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
